import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar, MapPin, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { computeWorkHours } from "@/utils/attendanceHours";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function MyAttendanceList() {
  const { token } = useAuth();
  const today = new Date();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(today.getFullYear()));
  const [month, setMonth] = useState(String(today.getMonth() + 1)); // 1-12
  const [day, setDay] = useState("all"); // 1-31 or 'all'

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      // Fetch a full year of records (max 365 per API contract)
      const res = await axios.get(`${API}/hr/attendance/my-records?days=365`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data?.records || []);
    } catch (e) {
      toast.error("Failed to load attendance records");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setYear(String(today.getFullYear()));
    setMonth(String(today.getMonth() + 1));
    setDay("all");
  };

  // Derived available year options from data (plus current year)
  const yearOptions = useMemo(() => {
    const set = new Set([String(today.getFullYear())]);
    records.forEach((r) => {
      if (r.date) set.add(r.date.slice(0, 4));
    });
    return Array.from(set).sort().reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  // Filter records by selected year + month + (optionally) day
  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (!r.date) return false;
      const [y, m, d] = r.date.split("-");
      if (year !== "all" && y !== year) return false;
      if (month !== "all" && parseInt(m, 10) !== parseInt(month, 10)) return false;
      if (day !== "all" && parseInt(d, 10) !== parseInt(day, 10)) return false;
      return true;
    });
  }, [records, year, month, day]);

  // Summary numbers for the filtered range
  const summary = useMemo(() => {
    const present = filtered.filter((r) => r.status === "Present").length;
    const absent = filtered.filter((r) => r.status === "Absent").length;
    const leave = filtered.filter((r) => r.status === "Leave").length;
    const halfDay = filtered.filter((r) => r.status === "Half Day").length;
    return { total: filtered.length, present, absent, leave, halfDay };
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="my-attendance-list">
      {/* Header + Summary */}
      <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-brillien-600" />
              My Attendance Records
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              View your check-in / check-out history. All times shown in IST.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap text-sm">
            <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1">
              Present: {summary.present}
            </Badge>
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
              Half Day: {summary.halfDay}
            </Badge>
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
              Leave: {summary.leave}
            </Badge>
            <Badge className="bg-red-100 text-red-700 px-3 py-1">
              Absent: {summary.absent}
            </Badge>
            <Badge variant="outline" className="px-3 py-1">
              Total: {summary.total}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="my-att-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={y}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="my-att-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {MONTHS.map((m, idx) => (
                  <SelectItem key={m} value={String(idx + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Day</Label>
            <Select value={day} onValueChange={setDay}>
              <SelectTrigger data-testid="my-att-day-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Days</SelectItem>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {String(d).padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={resetFilters}
              data-testid="my-att-reset-filters"
              className="w-full"
            >
              <RotateCcw className="h-4 w-4 mr-2" /> Reset
            </Button>
          </div>
        </div>
      </Card>

      {/* Records Table */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Check-In</TableHead>
              <TableHead>Check-Out</TableHead>
              <TableHead>Total Hours</TableHead>
              <TableHead>Check-In Location</TableHead>
              <TableHead>Check-Out Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Marked By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  No attendance records found for the selected filters.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((r) => (
                <TableRow key={r.id} data-testid={`my-att-row-${r.date}`}>
                  <TableCell className="font-medium dark:text-white">{r.date}</TableCell>
                  <TableCell className="font-mono">
                    {r.check_in && r.check_in !== "00:00" ? r.check_in : "—"}
                  </TableCell>
                  <TableCell className="font-mono">
                    {r.check_out && r.check_out !== "00:00" ? r.check_out : "—"}
                  </TableCell>
                  <TableCell className="font-medium dark:text-slate-200" data-testid={`my-att-hours-${r.date}`}>
                    {computeWorkHours(r.check_in, r.check_out, r.status)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.check_in_location_name ? (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-brillien-600" />
                        {r.check_in_location_name}
                      </span>
                    ) : (
                      r.location || "—"
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.check_out_location_name ? (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-brillien-600" />
                        {r.check_out_location_name}
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        r.status === "Present"
                          ? "bg-emerald-100 text-emerald-700"
                          : r.status === "Absent"
                          ? "bg-red-100 text-red-700"
                          : r.status === "Leave"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700"
                      }
                    >
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {r.marked_by || "—"}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
